<template>
  <div
    class="ui-fullscreen-layout"
    :class="[
      !$slots.sidebar && 'ui-fullscreen-layout--no-sidebar',
      sidebarPosition === 'right' && 'ui-fullscreen-layout--sidebar-right',
      padded && 'ui-fullscreen-layout--padded'
    ]"
  >
    <aside
      v-if="$slots.sidebar"
      class="ui-fullscreen-layout__sidebar"
    >
      <slot name="sidebar" />
    </aside>

    <section class="ui-fullscreen-layout__workspace">
      <header
        v-if="$slots.header"
        class="ui-fullscreen-layout__header"
      >
        <slot name="header" />
      </header>

      <main class="ui-fullscreen-layout__content">
        <slot />
      </main>
    </section>

    <slot name="overlays" />
  </div>
</template>

<script setup lang="ts">
type SidebarPosition = 'left' | 'right';

withDefaults(
  defineProps<{
    sidebarPosition?: SidebarPosition;
    padded?: boolean;
  }>(),
  {
    sidebarPosition: 'left',
    padded: false
  }
);
</script>

<style scoped>
.ui-fullscreen-layout {
  display: grid;
  width: 100%;
  min-width: 0;
  min-height: 100vh;
  background: var(--ui-surface);
  color: var(--ui-foreground);
  grid-template-columns: auto minmax(0, 1fr);
  overflow: hidden;
}

.ui-fullscreen-layout--sidebar-right {
  grid-template-columns: minmax(0, 1fr) auto;
}

.ui-fullscreen-layout--no-sidebar,
.ui-fullscreen-layout--no-sidebar.ui-fullscreen-layout--sidebar-right {
  grid-template-columns: minmax(0, 1fr);
}

.ui-fullscreen-layout--sidebar-right .ui-fullscreen-layout__sidebar {
  grid-column: 2;
  grid-row: 1;
}

.ui-fullscreen-layout--no-sidebar .ui-fullscreen-layout__workspace,
.ui-fullscreen-layout--sidebar-right .ui-fullscreen-layout__workspace {
  grid-column: 1;
  grid-row: 1;
}

.ui-fullscreen-layout--padded {
  padding: var(--ui-space-4);
}

.ui-fullscreen-layout__sidebar {
  position: relative;
  z-index: 2;
  min-height: 0;
  min-width: 0;
  border-right: 1px solid var(--ui-border);
  background: var(--ui-surface);
  overflow: hidden;
}

.ui-fullscreen-layout--sidebar-right .ui-fullscreen-layout__sidebar {
  border-right: 0;
  border-left: 1px solid var(--ui-border);
}

.ui-fullscreen-layout__workspace {
  display: flex;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
  overflow: hidden;
}

.ui-fullscreen-layout__header {
  flex: 0 0 auto;
  border-bottom: 1px solid var(--ui-border);
  background: var(--ui-surface);
}

.ui-fullscreen-layout__content {
  position: relative;
  display: flex;
  min-width: 0;
  min-height: 0;
  flex: 1 1 auto;
  overflow: hidden;
}

@media (max-width: 768px) {
  .ui-fullscreen-layout,
  .ui-fullscreen-layout--sidebar-right {
    grid-template-columns: minmax(0, 1fr);
  }

  .ui-fullscreen-layout__sidebar,
  .ui-fullscreen-layout--sidebar-right .ui-fullscreen-layout__sidebar {
    grid-column: 1;
    grid-row: 1;
    border-right: 0;
    border-left: 0;
    border-bottom: 1px solid var(--ui-border);
  }

  .ui-fullscreen-layout__workspace,
  .ui-fullscreen-layout--sidebar-right .ui-fullscreen-layout__workspace {
    grid-column: 1;
    grid-row: 2;
  }
}
</style>
